import { useEffect, useRef } from "react";
import { X, ChevronLeft } from "lucide-react";
import ScanVoucherContent from "./ScanVoucherContent";

const PRIMARY = "#2D5BFF";
const PRIMARY_DARK = "#1B3DBF";
const GRADIENT = `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`;

export default function ScanVoucherModal({ isOpen, onClose, isMobile = false, onSaved }) {
  const overlayRef = useRef(null);
  const sheetRef = useRef(null);

  // Lock body scroll + Escape close
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  // Mobile: swipe-down on drag handle to close
  const handleDragStart = (e) => {
    if (!isMobile) return;
    const startY = e.touches ? e.touches[0].clientY : e.clientY;
    const move = (ev) => {
      const y = ev.touches ? ev.touches[0].clientY : ev.clientY;
      const dy = y - startY;
      if (sheetRef.current && dy > 0) {
        sheetRef.current.style.transform = `translateY(${dy}px)`;
        sheetRef.current.style.transition = "none";
      }
    };
    const end = (ev) => {
      const y = ev.changedTouches ? ev.changedTouches[0].clientY : ev.clientY;
      const dy = y - startY;
      if (sheetRef.current) {
        sheetRef.current.style.transition = "transform 0.25s ease-out";
        if (dy > 100) {
          onClose();
        } else {
          sheetRef.current.style.transform = "translateY(0)";
        }
      }
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", end);
      document.removeEventListener("touchmove", move);
      document.removeEventListener("touchend", end);
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", end);
    document.addEventListener("touchmove", move);
    document.addEventListener("touchend", end);
  };

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  // Shared header
  const Header = () => (
    <div
      className="relative flex items-center"
      style={{
        background: GRADIENT,
        padding: isMobile ? "20px 16px" : "20px 24px",
        borderTopLeftRadius: 22,
        borderTopRightRadius: 22,
      }}
    >
      {isMobile ? (
        <button
          onClick={onClose}
          className="flex items-center justify-center"
          style={{
            width: 36,
            height: 36,
            borderRadius: 999,
            backgroundColor: "rgba(255,255,255,0.18)",
            color: "#FFFFFF",
            marginRight: 12,
          }}
          data-testid="scan-back-btn"
          aria-label="Close"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      ) : null}
      <h2
        style={{
          color: "#FFFFFF",
          fontSize: 22,
          fontWeight: 700,
          fontFamily: "Manrope, sans-serif",
        }}
      >
        Add Voucher
      </h2>
      {!isMobile ? (
        <button
          onClick={onClose}
          className="absolute flex items-center justify-center"
          style={{
            top: 16,
            right: 16,
            width: 36,
            height: 36,
            borderRadius: 999,
            backgroundColor: "rgba(255,255,255,0.18)",
            color: "#FFFFFF",
          }}
          data-testid="scan-close-btn"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      ) : null}
    </div>
  );

  // ── Mobile bottom sheet ──
  if (isMobile) {
    return (
      <div
        ref={overlayRef}
        onClick={handleOverlayClick}
        className="fixed inset-0 z-50 flex items-end animate-fadeIn"
        style={{ backgroundColor: "rgba(15,23,42,0.5)" }}
        data-testid="scan-voucher-overlay"
      >
        <div
          ref={sheetRef}
          className="w-full bg-white animate-slideUp flex flex-col"
          style={{
            height: "90vh",
            borderTopLeftRadius: 22,
            borderTopRightRadius: 22,
            boxShadow: "0 -8px 30px rgba(0,0,0,0.15)",
            overflow: "hidden",
          }}
          data-testid="scan-voucher-sheet"
        >
          {/* Drag handle */}
          <div
            className="flex justify-center pt-2.5 pb-2 cursor-grab active:cursor-grabbing"
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
            data-testid="drag-handle"
          >
            <div style={{ width: 40, height: 4, backgroundColor: "#D1D5DB", borderRadius: 999 }} />
          </div>

          <Header />

          <div className="flex-1 overflow-y-auto" style={{ padding: 18, overflowX: 'hidden' }}>
            <ScanVoucherContent onComplete={onClose} onSaved={onSaved} isMobile={true} />
          </div>
        </div>

        <style>{`
          @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
          @keyframes slideUp { from { transform: translateY(100%) } to { transform: translateY(0) } }
          .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
          .animate-slideUp { animation: slideUp 0.3s ease-out; }
        `}</style>
      </div>
    );
  }

  // ── Web centred modal ──
  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fadeIn"
      style={{ backgroundColor: "rgba(15,23,42,0.5)", backdropFilter: "blur(2px)" }}
      data-testid="scan-voucher-overlay"
    >
      <div
        className="bg-white animate-scaleIn flex flex-col"
        style={{
          width: "100%",
          maxWidth: 720,
          maxHeight: "85vh",
          borderRadius: 22,
          overflow: "hidden",
          boxShadow: "0 24px 48px rgba(0,0,0,0.18)",
        }}
        data-testid="scan-voucher-sheet"
      >
        <Header />
        <div className="flex-1 overflow-y-auto" style={{ padding: 24, overflowX: 'hidden' }}>
          <ScanVoucherContent onComplete={onClose} onSaved={onSaved} isMobile={false} />
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.96); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
        .animate-scaleIn { animation: scaleIn 0.22s ease-out; }
      `}</style>
    </div>
  );
}